import type { TreeNode } from '../lib/api.js';

/**
 * Collapse and reveal over a flat, ordered node list.
 *
 * The tree arrives as document order plus a depth, which is all the rail needs:
 * a node's children are the run of deeper nodes that follows it, up to the next
 * node at its own depth or shallower. That avoids building a nested structure
 * only to flatten it again for rendering, and it keeps the list virtualisable.
 */

export interface RailRow {
  node: TreeNode;
  /** Index into the source array, so selection survives filtering. */
  index: number;
  hasChildren: boolean;
  expanded: boolean;
}

/** Does this node have any children in document order? */
export function hasChildren(nodes: readonly TreeNode[], index: number): boolean {
  const next = nodes[index + 1];
  return next !== undefined && next.depth > nodes[index]!.depth;
}

/**
 * Is this node something a person would navigate *to*?
 *
 * Paragraphs are not. 435 of the 674 nodes are lettered paragraphs, and an
 * outline that lists them is answering a question nobody asked: clicking
 * `10.1(c)` used to open a page whose entire content was "Casual employment;
 * or". Four words is not a unit anyone reads, and burying `10.2 Terms of
 * Engagement` among four such rows made the outline harder to scan than the
 * PDF it replaced.
 *
 * So a paragraph is a *reading* unit, rendered inline with the clause it
 * belongs to, and the outline stops at the level where a heading exists. It
 * stays fully addressable — a citation to 10.1(c) still resolves, still deep
 * links, and still anchors an annotation. What changes is only that it is no
 * longer a destination in its own right.
 */
export function isNavigable(node: TreeNode): boolean {
  return node.kind !== 'paragraph';
}

/** The outline's rows: the document without its paragraph-level detail. */
export function navigableNodes(nodes: readonly TreeNode[]): TreeNode[] {
  return nodes.filter(isNavigable);
}

/**
 * The rows actually shown, given which nodes are expanded.
 *
 * A node is hidden when any ancestor above it is collapsed. Walking the depth
 * sequence is enough to know that: once a collapsed node is seen, everything
 * deeper is skipped until the depth returns to its level.
 */
export function visibleRows(
  nodes: readonly TreeNode[],
  expanded: ReadonlySet<string>,
): RailRow[] {
  const rows: RailRow[] = [];
  let hiddenBelow: number | null = null;

  for (const [index, node] of nodes.entries()) {
    if (hiddenBelow !== null) {
      if (node.depth > hiddenBelow) continue;
      hiddenBelow = null;
    }

    const children = hasChildren(nodes, index);
    const isExpanded = expanded.has(node.number);
    rows.push({ node, index, hasChildren: children, expanded: isExpanded });

    if (children && !isExpanded) hiddenBelow = node.depth;
  }

  return rows;
}

/**
 * Every ancestor of a clause, so selecting one by deep link opens the path to
 * it rather than leaving it hidden inside a collapsed Part.
 */
export function ancestorsOf(nodes: readonly TreeNode[], number: string): string[] {
  const index = nodes.findIndex((n) => n.number === number);
  if (index < 0) return [];

  const path: string[] = [];
  let depth = nodes[index]!.depth;
  for (let i = index - 1; i >= 0 && depth > 0; i--) {
    const node = nodes[i]!;
    if (node.depth < depth) {
      path.unshift(node.number);
      depth = node.depth;
    }
  }
  return path;
}

/** The breadcrumb for a clause: its ancestors, then itself. */
export function breadcrumb(nodes: readonly TreeNode[], number: string): TreeNode[] {
  const names = new Set([...ancestorsOf(nodes, number), number]);
  return nodes.filter((n) => names.has(n.number));
}

/**
 * The node the reader should open for a given target.
 *
 * A citation resolves to whatever it names, and half the agreement's
 * cross-references name paragraphs — "subject to clause 13.4(b)(ii)". Those
 * still have to work, but opening a page containing one lettered fragment
 * would strand the reader with no context. So the reader opens the nearest
 * ancestor that carries a heading, and the paragraph is shown in place within
 * it, where it can be read against its siblings.
 */
export function readerTarget(nodes: readonly TreeNode[], number: string): string {
  const node = nodes.find((n) => n.number === number);
  if (!node || isNavigable(node)) return number;

  const ancestors = ancestorsOf(nodes, number);
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const candidate = nodes.find((n) => n.number === ancestors[i]);
    if (candidate && isNavigable(candidate)) return candidate.number;
  }
  return number;
}

/**
 * A navigable node and every paragraph beneath it, in document order.
 *
 * This is what the reader renders as one page: clause 10.2 together with
 * 10.2(a), (b) and (c), which is how the clause reads on the printed page and
 * how anyone arguing about it needs to see it.
 *
 * Stops at the next navigable node rather than at a depth, so a clause
 * containing a nested sub-list keeps the whole list.
 */
export function readingBlock(nodes: readonly TreeNode[], number: string): TreeNode[] {
  const start = nodes.findIndex((n) => n.number === number);
  if (start < 0) return [];

  const block = [nodes[start]!];
  for (let i = start + 1; i < nodes.length; i++) {
    const node = nodes[i]!;
    // A deeper navigable node — 10.2.1 under 10.2 — is its own page.
    if (isNavigable(node)) break;
    if (node.depth <= nodes[start]!.depth) break;
    block.push(node);
  }
  return block;
}
